import React, { useMemo } from 'react';
import { useRouteMatch } from 'react-router-dom';
import {
  AdminDashboardIcon,
  BasicPropertiesIcon,
  CardsIcon,
  ClockTickIcon,
  CommunicationIcon,
  ContactsSecurityIcon,
  FeaturesIcon,
  FilesImportsIcon,
  FinalSetupIcon,
  FinancialDetailsIcon,
  HandIcon,
  OrgLockIcon,
  SetupOutlinedIcon,
  WarningIcon,
} from '@common-fe/common-fe';

import { FeatureFlagKeys, OrganizationTypes } from '@/common/constants';
import Permissions from '@/common/permissions';
import { TOCSidebarProps } from '@/modules/core/components/TOCSidebar/TOCSidebar.types';
import { useCurrentOrganization } from '@/modules/core/hooks';
import { useHasAccess } from '@/modules/core/hooks';
import useGetOrganizationById from '@/modules/core/hooks/useGetOrganizationById';
import useAddressFields from '@/modules/employer/components/SetupEmployer/Address/useAddressFields';
import { useCardSetupForm } from '@/modules/employer/components/SetupEmployer/Cards/CardSetup/hooks/useCardSetupForm';
import usePendingDocsEscalationFields from '@/modules/employer/components/SetupEmployer/Cards/CardSetup/hooks/usePendingDocsEscalationFields';
import { useEmployerIdFields } from '@/modules/employer/components/SetupEmployer/EmployeeId/useEmployerIdFields';
import useEmployerFields from '@/modules/employer/components/SetupEmployer/Employer/useEmployerFields';
import useAccountUsageFields from '@/modules/employer/components/SetupEmployer/FinancialDetails/BankInformation/AccountUsage/hooks/useAccountUsageFields';
import { useAccountUsageStore } from '@/modules/employer/components/SetupEmployer/FinancialDetails/BankInformation/AccountUsage/stores';
import useBanksList from '@/modules/employer/components/SetupEmployer/FinancialDetails/BankInformation/BanksList/useBanksList';
import { useFundingModelFields } from '@/modules/employer/components/SetupEmployer/FinancialDetails/FundingModel/hooks';
import useGeneralInformationFields from '@/modules/employer/components/SetupEmployer/GeneralInformation/useGeneralInformationFields';
import { useGetHiddenFields, useSetupEmployerMode } from '@/modules/employer/components/SetupEmployer/hooks';
import useBusinessEntityTypesQuery from '@/modules/employer/components/SetupEmployer/queries/useBusinessEntityTypes.query';
import useCheckWritingsQuery from '@/modules/employer/components/SetupEmployer/queries/useCheckWritings.query';
import useElectronicFundingReplenishmentsQuery from '@/modules/employer/components/SetupEmployer/queries/useElectronicFundingReplenishments.query';
import useHsaCustodianQuery from '@/modules/employer/components/SetupEmployer/queries/useHsaCustodian.query';
import { useSaveTypeStore } from '@/modules/employer/components/SetupEmployer/stores';
import { EmployerSetupModes } from '@/modules/employer/employerSetupModes.types';
import { useBankMethodsQuery } from '@/modules/employer/hooks/index';
import { useEmployerTerminationReactivationAccess } from '@/modules/employer/hooks/useEmployerTerminationReactivationAccess';
import useGetEmployer from '@/modules/employer/hooks/useGetEmployer.query';
import { useSetupEmployerStore } from '@/modules/employer/store';
import { useStore } from '@/modules/employer/store/useSetupEmployer.store';
import { useIsCardClaimSuspensionAvailable, useIsFraudPaymentHoldAvailable } from '@/modules/featureFlags';
import { useEnvSpecificAvailable } from '@/utils';
import { fieldArrToMap } from '@/utils/handlers';
import { useCalculateFillProgress } from '@/utils/hooks/useCalculateFillProgress';
import useReversedFeatureFlag from '@/utils/hooks/useFeatureFlag';

import { useEmailConfigurationStore } from '../components/SetupEmployer/Communication/EmailConfiguration/stores';
import { useCommunicationChannelStore } from '../components/SetupEmployer/Communication/stores/useCommunicationChannelStore';
import useGetExpressSetupLinksFieldsPermProps from '../components/SetupEmployer/Features/ExpressSetupLinks/hooks/useGetExpressSetupLinksFieldsPermProps';
import useOrphanConfigurationFieldsPermProps from '../components/SetupEmployer/Features/OrphanConfiguration/hooks/useOrphanConfigurationFieldsPermProps';
import useGetAllowRenewalsFieldsPermProps from '../components/SetupEmployer/Features/PlansRenewal/hooks/useGetAllowRenewalsFieldsPermProps';
import useGetReimbursementFieldsPermProps from '../components/SetupEmployer/Features/Reimbursement/hooks/useGetReimbursementFieldsPermProps';
import { EmployerStatus } from '../employer.constants';

const REQUIRED_PERMISSIONS = [{ permission: Permissions.ORG_LOCK }];

/* eslint-disable-next-line @typescript-eslint/no-explicit-any */
const useBasicPropertiesFillProgress = (formattedData: any) => {
  const { address, employer, generalInfo, employeeId } = useSetupEmployerStore();
  const { data: businessTypes } = useBusinessEntityTypesQuery();
  const { data: hsaCustodians } = useHsaCustodianQuery();
  const {
    formattedData: { name },
  } = useGetOrganizationById(formattedData.grandparentId);
  const { pendingStatus } = useSaveTypeStore();
  const { viewMode } = useSetupEmployerMode();

  const addressFieldsDefaultValues = useMemo(
    () => ({
      addresses: [
        {
          line1: address?.street,
          line2: address?.street_2,
          city: address?.city,
          state: address?.state,
          zipcode: address?.zipCode,
        },
      ],
    }),
    [address]
  );
  const employerFields = useEmployerFields(
    !pendingStatus,
    {
      name: employer?.employerName,
      externalIdentifier: employer?.employerId,
    },
    name
  );

  const addressFields = useAddressFields(!pendingStatus, addressFieldsDefaultValues);
  const generalInformationFields = useGeneralInformationFields(
    [],
    businessTypes,
    hsaCustodians,
    !pendingStatus,
    {
      shortName: generalInfo?.dba,
      businessEntityType: generalInfo?.businessType,
      organizationPhone: {
        number: generalInfo?.phoneNumber,
        phoneExtension: generalInfo?.phoneNumberExtension,
      },
      overridableProperties: {
        themeId: generalInfo?.themeId,
        hsaCustodianId: generalInfo?.hsaCustodianId,
        url: generalInfo?.url,
        ssoOnly: generalInfo?.ssoOnly,
      },
      normalProperties: {
        accountManagerId: generalInfo?.accountManagerId,
        taxId: generalInfo?.taxID,
        implementationManagerId: generalInfo?.implementationManagerId,
      },
    }
  );

  const employerIdFields = useEmployerIdFields({
    viewMode,
    options: [],
    employerId: employeeId?.eeid,
  });

  return useCalculateFillProgress(
    fieldArrToMap(employerFields),
    fieldArrToMap(addressFields),
    fieldArrToMap(generalInformationFields),
    fieldArrToMap(employerIdFields)
  );
};

const useFundingModelFillProgress = () => {
  const { fundingModel, primaryContactId, hasOneContact } = useSetupEmployerStore();
  const accountUsageStore = useAccountUsageStore();
  const { data: checkWritings } = useCheckWritingsQuery();
  const { data: electronicFundingReplenishments } = useElectronicFundingReplenishmentsQuery();
  const { isEmpty: isEmptyBankList } = useBanksList();
  const { methods } = useBankMethodsQuery();
  const { list: banks } = useBanksList();

  const fundingModelFields = useFundingModelFields(checkWritings, electronicFundingReplenishments, {
    overridableProperties: {
      electronicFundingModeId: fundingModel.electronicFundingReplenishment,
      checkFundingModel: fundingModel.checkWriting,
    },
  });

  const accountUsageFields = useAccountUsageFields(banks, methods, {
    overridableProperties: {
      externalAdministratorFeesAccountMethod: accountUsageStore.state.administratorFeesMethod,
      externalPayrollContributionsAccountMethod: accountUsageStore.state.payrollContributionMethod,
      externalReplenishmentAccountMethod: accountUsageStore.state.fundingReplenishmentMethod,
      payrollContributionsHoldPeriod: accountUsageStore.state.holdPeriod,
      eeContributionsHoldPeriod: accountUsageStore.state.eeContributionsHoldPeriod,
    },
    normalProperties: {
      externalAdministratorFeesAccount: accountUsageStore.state.administratorFeesBank,
      externalPayrollContributionsAccount: accountUsageStore.state.payrollContributionBank,
      externalReplenishmentAccount: accountUsageStore.state.fundingReplenishmentBank,
    },
  });

  const progress = useCalculateFillProgress(
    fieldArrToMap(fundingModelFields),
    fieldArrToMap(accountUsageFields)
  );
  let total = progress.total + 1;
  let { passed } = progress;

  if (!isEmptyBankList) passed += 1;
  if (hasOneContact) total += 1;
  if (hasOneContact && primaryContactId) passed += 1;

  return useMemo(
    () => ({
      total,
      passed,
    }),
    [total, passed]
  );
};

const useCommunicationFillProgress = () => {
  const communicationChannelStoreState = useCommunicationChannelStore((state) => state.state);
  const emailConfigurationStore = useEmailConfigurationStore((state) => state.state);
  return useCalculateFillProgress(communicationChannelStoreState, emailConfigurationStore);
};

const useCardFillProgress = (isPendingDocsEscalationVisible?: boolean) => {
  const fields = useCardSetupForm();
  const pendingDocsEscalationFields = usePendingDocsEscalationFields();
  return useCalculateFillProgress(
    fieldArrToMap(isPendingDocsEscalationVisible ? [...fields, ...pendingDocsEscalationFields] : fields)
  );
};

interface Params {
  isPartner?: boolean;
}
export const useTOCConfiguration = (params?: Params): TOCSidebarProps['stepsMap'] => {
  const isOnlyForDevAndUAT = useEnvSpecificAvailable({
    dev: true,
    uat: true,
  });
  const { hasAccessTerminateEmployer, hasAccessReactivateEmployer } =
    useEmployerTerminationReactivationAccess();
  const { hasOneContact } = useSetupEmployerStore();
  useRouteMatch();
  const hasAccess = useHasAccess(REQUIRED_PERMISSIONS);

  const { mode } = useSetupEmployerMode();
  const {
    observingOrganization: { type, status },
  } = useCurrentOrganization();
  const isSystemLevel = useMemo(() => type === OrganizationTypes.system, [type]);
  const isPartnerLevel = useMemo(() => type === OrganizationTypes.partner, [type]);
  const isDistributorLevel = useMemo(() => type === OrganizationTypes.distributor, [type]);
  const { formattedData } = useGetEmployer();
  const basicPropertiesFillProgress = useBasicPropertiesFillProgress(formattedData);
  const fundingModelFillProgress = useFundingModelFillProgress();
  const communicationFillProgress = useCommunicationFillProgress();
  const isCardClaimSuspensionAvailable = useIsCardClaimSuspensionAvailable();
  const cardOffered = useStore((state) => state.cardsSetup?.cardOffered);
  const isPendingDocsEscalationVisible = isCardClaimSuspensionAvailable && Boolean(cardOffered);
  const cardFillProgress = useCardFillProgress(isPendingDocsEscalationVisible);
  // const showPreFund = useMemo(() => accountUsageStore.state.fundingReplenishmentBank, [accountUsageStore]);
  const { hasFields: hasReimbursementFields } = useGetReimbursementFieldsPermProps();
  const isFraudPaymentHoldAvailable = useIsFraudPaymentHoldAvailable();
  const isFraudThresholdVisible =
    isFraudPaymentHoldAvailable &&
    Boolean(
      formattedData?.overridableProperties?.isReimbursementAllowed ||
      formattedData?.overridableProperties?.isBillPayAllowed
    );
  const {
    isEmployeeIdHidden,
    isBrandingHidden,
    isHelpSectionHidden,
    isLinkManagementHidden,
    isEmailConfiguratorHidden,
    isMobileCustomizationHidden,
    isCommunicationSectionHidden,
    isFileManagerSectionHidden,
    isTechnicalSettingsSectionHidden,
    isFundingModeSectionHidden,
    isInvoicingDetailsSectionHidden,
    isPayrollContributionSectionHidden,
    isIndividualContributionSectionHidden,
    isAdminContributionsSectionHidden,
    isFundingReplenishmentSectionHidden,
    isPrefundSectionHidden,
  } = useGetHiddenFields();
  const basicConfig: TOCSidebarProps['stepsMap'] = useMemo(
    () => ({
      basic_properties: {
        title: 'Basic Properties',
        required: true,
        icon: <BasicPropertiesIcon color="iconAccent" />,
        sections: {
          address: 'Address',
          general_information: 'General Information',
          ...(isEmployeeIdHidden ? {} : { employee_ID: 'Employee ID' }),
          ...(isBrandingHidden ? {} : { branding: 'Logo' }),

          ...(params?.isPartner && !isEmailConfiguratorHidden
            ? { email_configuration: 'Email Theme Configuration' }
            : {}),
          email_template_configuration_list: 'Email Template Configuration',
          ...(params?.isPartner && !isMobileCustomizationHidden
            ? { mobile_app_customization: 'Mobile App Customization' }
            : {}),

          ...(isLinkManagementHidden ? {} : { link_management_section: 'Link Management' }),

          ...(isHelpSectionHidden ? {} : { help_section: 'Help' }),
        },
        process: basicPropertiesFillProgress,
      },

      contacts_security: {
        title: 'Contacts/Security',
        required: false,
        icon: <ContactsSecurityIcon color="iconAccent" />,
        sections: {
          contact_information: 'Contact Information',
          multi_factor_authentication: 'Multi-Factor Authentication',
        },
      },
      ...(isCommunicationSectionHidden
        ? {}
        : {
            communication: {
              title: 'Communication',
              required: false,
              icon: <CommunicationIcon color="iconAccent" />,
              sections: {
                ...(params?.isPartner ? { communication_channels: 'Communication methods' } : {}),
                ...(params?.isPartner ? { email_sms_communications: 'Email/Text communications' } : {}),
                statements: 'Statements',
              },
              process: communicationFillProgress,
            },
          }),
    }),
    [
      basicPropertiesFillProgress,
      communicationFillProgress,
      isBrandingHidden,
      isCommunicationSectionHidden,
      isEmailConfiguratorHidden,
      isEmployeeIdHidden,
      isHelpSectionHidden,
      isLinkManagementHidden,
      isMobileCustomizationHidden,
      params?.isPartner,
    ]
  );
  const { hasFields: hasOrphanConfigurationFields } = useOrphanConfigurationFieldsPermProps();
  const { hasFields: hasExpressSetupLinksFields } = useGetExpressSetupLinksFieldsPermProps();
  const { hasFields: hasAllowRenewalsFields } = useGetAllowRenewalsFieldsPermProps();
  const isPlansRenewalAvailable = useReversedFeatureFlag(FeatureFlagKeys.PLANS_RENEWAL, {
    dev: true,
    uat: true,
  });
  const employerSteps: TOCSidebarProps['stepsMap'] = useMemo(
    () => ({
      financial_details: {
        title: 'Financial Details',
        required: true,
        icon: <FinancialDetailsIcon color="iconAccent" />,
        sections: {
          ...(isFundingModeSectionHidden ? {} : { funding_model: 'Funding Model' }),
          ...(hasOneContact && !isInvoicingDetailsSectionHidden
            ? { invoicing_details: 'Invoicing Details' }
            : {}),
          bank_information: 'Bank Information',
          // administrator_fees: 'Partner Administrator Fees',

          ...(isPayrollContributionSectionHidden ? {} : { payroll_contributions: 'Payroll Contributions' }),
          ...(isIndividualContributionSectionHidden
            ? {}
            : { individual_contributions: 'Individual Contributions' }),

          ...(isOnlyForDevAndUAT && !isAdminContributionsSectionHidden
            ? {
                admin_contributions: 'Admin Contributions',
              }
            : {}),

          ...(isFundingReplenishmentSectionHidden ? {} : { funding_replenishment: 'Funding Replenishment' }),
          ...(isPrefundSectionHidden ? {} : { pre_fund: 'Pre-Fund' }),
          balance_adjustment: 'Balance Adjustment',
        },
        process: fundingModelFillProgress,
      },
      features: {
        title: 'Features',
        required: false,
        icon: <FeaturesIcon color="iconAccent" />,
        sections: {
          plan_year_section: 'Plan Year',
          ...(!isSystemLevel && !isPartnerLevel && !isDistributorLevel
            ? { payroll_group: 'Payroll Group' }
            : {}),
          ...(!isSystemLevel && !isPartnerLevel && !isDistributorLevel
            ? { payroll_calendar: 'Payroll Calendar' }
            : {}),
          ...(hasReimbursementFields ? { reimbursement_options: 'Reimbursement options' } : {}),
          ...(isFraudThresholdVisible ? { fraud_threshold: 'Fraud risk review thresholds' } : {}),
          ai_adjudication: 'AI Adjudication',
          ...(hasOrphanConfigurationFields ? { orphan_configuration: 'Orphan configuration' } : {}),
          ...(hasExpressSetupLinksFields
            ? {
                express_setup_links: 'Express Employer Setup Via External Link',
              }
            : {}),
          ...(isPlansRenewalAvailable && hasAllowRenewalsFields
            ? {
                plans_renewal: 'Plans Renewal',
              }
            : {}),
        },
      },
      cards: {
        title: 'Cards',
        required: false,
        icon: <CardsIcon color="iconAccent" />,
        sections: {
          cards_section: 'Cards',
          ...(isPendingDocsEscalationVisible ? { pending_docs_escalation: 'Pending Docs Escalation' } : {}),

          // copay_setup: 'Copay Setup',
        },
        process: cardFillProgress,
      },
      reports: {
        title: 'Admin Dashboard',
        required: false,
        icon: <AdminDashboardIcon color="iconAccent" />,
      },
      ...(isFileManagerSectionHidden
        ? {}
        : {
            files_imports: {
              title: 'Files/Imports',
              required: false,
              icon: <FilesImportsIcon color="iconAccent" />,
            },
          }),
      ...(isTechnicalSettingsSectionHidden
        ? {}
        : {
            technical_settings: {
              title: 'Technical Settings',
              required: false,
              icon: <SetupOutlinedIcon color="iconAccent" />,
            },
          }),

      claims_settings: {
        title: 'Claims',
        required: false,
        icon: <HandIcon color="iconAccent" />,
      },
    }),
    [
      isPrefundSectionHidden,
      isFundingReplenishmentSectionHidden,
      isAdminContributionsSectionHidden,
      isIndividualContributionSectionHidden,
      isPayrollContributionSectionHidden,
      isInvoicingDetailsSectionHidden,
      isFundingModeSectionHidden,
      hasOneContact,
      isOnlyForDevAndUAT,
      fundingModelFillProgress,
      isSystemLevel,
      isPartnerLevel,
      isDistributorLevel,
      hasReimbursementFields,
      isFraudThresholdVisible,
      hasOrphanConfigurationFields,
      hasExpressSetupLinksFields,
      cardFillProgress,
      isPendingDocsEscalationVisible,
      isFileManagerSectionHidden,
      isTechnicalSettingsSectionHidden,
      hasAllowRenewalsFields,
      isPlansRenewalAvailable,
    ]
  );
  const partnerSteps: TOCSidebarProps['stepsMap'] = useMemo(
    () => ({
      financial_details: {
        title: 'Financial Details',
        required: true,
        icon: <FinancialDetailsIcon color="iconAccent" />,
        sections: {
          ...(isFundingModeSectionHidden ? {} : { funding_model: 'Funding Model' }),
          ...(hasOneContact && !isInvoicingDetailsSectionHidden
            ? { invoicing_details: 'Invoicing Details' }
            : {}),
          bank_information: 'Bank Information',
          ...(isPartnerLevel || isSystemLevel ? { administrator_fees: 'Partner Administrator Fees' } : {}),
          ...(isPayrollContributionSectionHidden ? {} : { payroll_contributions: 'Payroll Contributions' }),
          ...(isIndividualContributionSectionHidden
            ? {}
            : { individual_contributions: 'Individual Contributions' }),
          ...(isOnlyForDevAndUAT && !isAdminContributionsSectionHidden
            ? {
                admin_contributions: 'Admin Contributions',
              }
            : {}),
          ...(isFundingReplenishmentSectionHidden ? {} : { funding_replenishment: 'Funding Replenishment' }),

          ...(isPrefundSectionHidden ? {} : { pre_fund: 'Pre-Fund' }),
          balance_adjustment: 'Balance Adjustment',
        },
        process: fundingModelFillProgress,
      },
      features: {
        title: 'Features',
        required: false,
        icon: <FeaturesIcon color="iconAccent" />,
        sections: {
          ...(isPartnerLevel ? { available_plan_templates: 'Available plan templates' } : {}),
          ...(hasReimbursementFields ? { reimbursement_options: 'Reimbursement options' } : {}),
          ...(isFraudThresholdVisible ? { fraud_threshold: 'Fraud risk review thresholds' } : {}),
          ai_adjudication: 'AI Adjudication',
          ...(hasOrphanConfigurationFields ? { orphan_configuration: 'Orphan configuration' } : {}),
          ...(isOnlyForDevAndUAT && !isSystemLevel && !isPartnerLevel && !isDistributorLevel
            ? { payroll_group: 'Payroll Group' }
            : {}),
          ...(!isSystemLevel && !isPartnerLevel && !isDistributorLevel
            ? { payroll_calendar: 'Payroll Calendar' }
            : {}),
          ...(hasExpressSetupLinksFields
            ? {
                express_setup_links: 'Express Employer Setup Via External Link',
              }
            : {}),
          ...(isPlansRenewalAvailable && hasAllowRenewalsFields
            ? {
                plans_renewal: 'Plans Renewal',
              }
            : {}),
        },
      },
      cards: {
        title: 'Cards',
        required: false,
        icon: <CardsIcon color="iconAccent" />,
        sections: {
          cards_section: 'Cards',
          ...(isPendingDocsEscalationVisible ? { pending_docs_escalation: 'Pending Docs Escalation' } : {}),
        },
        process: cardFillProgress,
      },
      reports: {
        title: 'Admin Dashboard',
        required: false,
        icon: <AdminDashboardIcon color="iconAccent" />,
      },

      ...(isFileManagerSectionHidden
        ? {}
        : {
            files_imports: {
              title: 'Files/Imports',
              required: false,
              icon: <FilesImportsIcon color="iconAccent" />,
            },
          }),

      ...(isTechnicalSettingsSectionHidden
        ? {}
        : {
            technical_settings: {
              title: 'Technical Settings',
              required: false,
              icon: <SetupOutlinedIcon color="iconAccent" />,
            },
          }),

      claims_settings: {
        title: 'Claims',
        required: false,
        icon: <HandIcon color="iconAccent" />,
      },
    }),
    [
      isPrefundSectionHidden,
      isFundingReplenishmentSectionHidden,
      isAdminContributionsSectionHidden,
      isIndividualContributionSectionHidden,
      isPayrollContributionSectionHidden,
      isInvoicingDetailsSectionHidden,
      isFundingModeSectionHidden,
      hasOneContact,
      isPartnerLevel,
      isSystemLevel,
      isOnlyForDevAndUAT,
      fundingModelFillProgress,
      hasReimbursementFields,
      isFraudThresholdVisible,
      hasOrphanConfigurationFields,
      isDistributorLevel,
      hasExpressSetupLinksFields,
      cardFillProgress,
      isPendingDocsEscalationVisible,
      isFileManagerSectionHidden,
      isTechnicalSettingsSectionHidden,
      hasAllowRenewalsFields,
      isPlansRenewalAvailable,
    ]
  );
  const canViewOrganization = useHasAccess([{ permission: Permissions.VIEW_ER }]);
  const items = useMemo(
    () => ({
      ...basicConfig,
      ...(mode === EmployerSetupModes.employer ? employerSteps : partnerSteps),

      // status: {
      //   title: 'Status',
      //   required: false,
      // },
      ...(canViewOrganization
        ? {
            activity_log: {
              title: 'Activity Log',
              required: false,
              icon: <ClockTickIcon style={{ width: '24px' }} color="iconAccent" />,
            },
          }
        : {}),
      final: {
        title: 'Final Setup',
        required: true,
        icon: <FinalSetupIcon color="iconAccent" />,
        hide: mode === EmployerSetupModes.system,
      },
      ...((mode === EmployerSetupModes.employer || mode === EmployerSetupModes.subsidiary) && {
        organisation_lock: {
          title: 'Organization Lock',
          required: false,
          icon: <OrgLockIcon color="iconAccent" />,
          hide: !hasAccess,
        },
      }),
      ...(mode === EmployerSetupModes.employer &&
        (hasAccessTerminateEmployer || hasAccessReactivateEmployer) && {
          danger_zone: {
            title: 'Danger Zone',
            required: false,
            icon: <WarningIcon />,
            hide:
              status !== EmployerStatus.Active &&
              status !== EmployerStatus.PendingTermination &&
              status !== EmployerStatus.Terminated &&
              status !== EmployerStatus.TerminationInProgress &&
              status !== EmployerStatus.PendingReactivation,
          },
        }),
    }),
    [
      basicConfig,
      employerSteps,
      mode,
      partnerSteps,
      hasAccess,
      status,
      canViewOrganization,
      hasAccessTerminateEmployer,
      hasAccessReactivateEmployer,
    ]
  );

  return items;
};
